import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import {
  Wrench, Plus, Trash, Calendar, ClockCountdown, Pencil, CheckSquare,
  WarningOctagon, VideoCamera, Fire, IdentificationBadge, FireExtinguisher,
  UsersFour, Desktop, Monitor as MonitorIcon, Camera, WifiHigh, Printer,
  Wind, Lightbulb, TelevisionSimple, Microphone, BatteryCharging, Package,
  CaretDown
} from "@phosphor-icons/react";

const SYSTEM_ICONS = {
  cctv: VideoCamera,
  pc: Desktop,
  atem: MonitorIcon,
  fire_system: Fire,
  access_control: IdentificationBadge,
  fire_extinguishers: FireExtinguisher,
  programmed_drills: UsersFour,
  camera: Camera,
  monitor: MonitorIcon,
  network_switch: WifiHigh,
  printer: Printer,
  hvac: Wind,
  lighting: Lightbulb,
  teleprompter: TelevisionSimple,
  audio: Microphone,
  ups: BatteryCharging,
  other: Package,
};

const STATUS_META = {
  pending:     { label: "Pending",     cls: "bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20" },
  in_progress: { label: "In progress", cls: "bg-blue-500/10 text-blue-400 border-blue-900/40" },
  completed:   { label: "Completed",   cls: "bg-emerald-500/10 text-emerald-400 border-emerald-900/40" },
};

const inputCls =
  "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]";

export default function Maintenance() {
  const [tasks, setTasks] = useState([]);
  const [systems, setSystems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterSystem, setFilterSystem] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterSystem !== "all") params.set("system_key", filterSystem);
    if (filterStatus !== "all") params.set("status", filterStatus);
    const { data } = await api.get(`/maintenance/scheduled?${params.toString()}`);
    setTasks(data);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/maintenance/systems");
      setSystems(data);
    })();
  }, []);
  useEffect(() => { load(); }, [filterSystem, filterStatus]);

  const advanceStatus = async (t) => {
    const next = t.status === "pending" ? "in_progress" : t.status === "in_progress" ? "completed" : "pending";
    await api.put(`/maintenance/scheduled/${t.id}`, { status: next });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this scheduled task?")) return;
    await api.delete(`/maintenance/scheduled/${id}`);
    load();
  };

  const counts = {
    pending:     tasks.filter((t) => t.status === "pending").length,
    in_progress: tasks.filter((t) => t.status === "in_progress").length,
    completed:   tasks.filter((t) => t.status === "completed").length,
    overdue:     tasks.filter((t) => t.status !== "completed" && t.scheduled_date && new Date(t.scheduled_date) < new Date()).length,
  };

  return (
    <div className="space-y-6 animate-fade-up max-w-6xl mx-auto" data-testid="maintenance-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5A623]">Schedule</div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            General <span className="text-[#F5A623]">Maintenance</span>
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Program maintenance for any device — CCTV, PC, ATEM, fire system, teleprompter, switch, and more. Assign responsibles, track status, leave comments.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          data-testid="scheduled-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#F5A623] hover:bg-[#E67E22] text-zinc-950 font-bold text-sm transition-colors"
        >
          <Plus size={16} weight="bold" /> Program Maintenance
        </button>
      </div>

      {/* Status chips summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Chip label="Pending"     value={counts.pending}     icon={ClockCountdown}    accent="text-[#F5A623]" testid="chip-pending" />
        <Chip label="In Progress" value={counts.in_progress} icon={Wrench}            accent="text-blue-400"  testid="chip-in-progress" />
        <Chip label="Completed"   value={counts.completed}   icon={CheckSquare}       accent="text-emerald-400" testid="chip-completed" />
        <Chip label="Overdue"     value={counts.overdue}     icon={WarningOctagon}    accent="text-red-400"   testid="chip-overdue" />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Filters</div>
        <select value={filterSystem} onChange={(e) => setFilterSystem(e.target.value)} className={inputCls + " max-w-[220px]"} data-testid="filter-system">
          <option value="all">All device types</option>
          {systems.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={inputCls + " max-w-[180px]"} data-testid="filter-status">
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {showForm && (
        <ScheduledForm
          systems={systems}
          initial={editing}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {/* Task list */}
      <div className="grid grid-cols-1 gap-3" data-testid="tasks-list">
        {loading && <div className="p-6 text-center text-sm text-zinc-500">Loading…</div>}
        {!loading && tasks.length === 0 && !showForm && (
          <div className="p-10 text-center rounded-lg bg-zinc-900/40 border border-dashed border-zinc-800 text-sm text-zinc-500">
            <Calendar size={28} className="mx-auto text-zinc-700 mb-2" weight="duotone" />
            No maintenance programmed yet. Hit <span className="text-[#F5A623] font-bold">Program Maintenance</span> above.
          </div>
        )}
        {!loading && tasks.map((t) => {
          const meta = STATUS_META[t.status] || STATUS_META.pending;
          const system = systems.find((s) => s.key === t.system_key);
          const Icon = SYSTEM_ICONS[t.system_key] || Wrench;
          const date = new Date(t.scheduled_date);
          const overdue = t.status !== "completed" && date < new Date();
          return (
            <article key={t.id} className="p-5 rounded-lg bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors" data-testid={`task-${t.id}`}>
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-md bg-zinc-800 grid place-items-center text-[#F5A623] flex-shrink-0">
                  <Icon size={20} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{system?.name || "General"}</div>
                      <h3 className="text-lg font-bold text-zinc-100 truncate">{t.title}</h3>
                      <div className="text-xs text-zinc-400 mt-0.5 font-mono">{t.device_name}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] tracking-[0.15em] uppercase font-bold border ${meta.cls}`}>
                        {meta.label}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-mono border ${overdue ? "bg-red-500/10 text-red-400 border-red-900/40" : "bg-zinc-800 text-zinc-300 border-zinc-700"}`}>
                        <ClockCountdown size={11} weight="bold" /> {date.toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {t.details && <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{t.details}</p>}

                  {t.responsibles?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {t.responsibles.map((r, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-zinc-800 border border-zinc-700 text-xs">
                          <span className="h-5 w-5 rounded-full bg-[#F5A623]/20 text-[#F5A623] grid place-items-center text-[10px] font-bold">
                            {r.name?.charAt(0) || "?"}
                          </span>
                          <span className="text-zinc-200">{r.name}</span>
                          {r.role && <span className="text-zinc-500">· {r.role}</span>}
                          {r.email && <span className="hidden md:inline text-zinc-600 font-mono">· {r.email}</span>}
                        </span>
                      ))}
                    </div>
                  )}

                  {t.comments && (
                    <div className="mt-3 p-3 rounded-md bg-zinc-950 border border-zinc-800">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Comments</div>
                      <p className="text-xs text-zinc-300 whitespace-pre-wrap">{t.comments}</p>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button onClick={() => advanceStatus(t)} data-testid={`task-advance-${t.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-zinc-800 border border-zinc-700 text-xs text-zinc-200 hover:bg-zinc-700 transition-colors">
                      <CheckSquare size={12} /> {t.status === "pending" ? "Start" : t.status === "in_progress" ? "Mark Completed" : "Reset"}
                    </button>
                    <button onClick={() => { setEditing(t); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`task-edit-${t.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-zinc-800 border border-zinc-700 text-xs text-zinc-200 hover:bg-zinc-700 transition-colors">
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => del(t.id)} data-testid={`task-delete-${t.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-zinc-900 border border-red-900/50 text-xs text-red-400 hover:bg-red-950/30 transition-colors">
                      <Trash size={12} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function Chip({ label, value, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid} className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800 flex items-center gap-3">
      <div className={`h-9 w-9 rounded-md bg-zinc-800 grid place-items-center ${accent}`}>
        <Icon size={16} weight="duotone" />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{label}</div>
        <div className="font-mono text-xl text-zinc-100">{value}</div>
      </div>
    </div>
  );
}

function ScheduledForm({ initial, systems, onCancel, onSaved }) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(() => ({
    title: initial?.title || "",
    device_name: initial?.device_name || "",
    system_key: initial?.system_key || "",
    details: initial?.details || "",
    scheduled_date: initial?.scheduled_date ? initial.scheduled_date.substring(0, 10) : "",
    comments: initial?.comments || "",
    status: initial?.status || "pending",
    responsibles: initial?.responsibles?.length ? initial.responsibles : [{ name: "", email: "", role: "" }],
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateResp = (idx, patch) =>
    setForm((f) => ({ ...f, responsibles: f.responsibles.map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  const addResp = () => setForm((f) => ({ ...f, responsibles: [...f.responsibles, { name: "", email: "", role: "" }] }));
  const removeResp = (idx) => setForm((f) => ({ ...f, responsibles: f.responsibles.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      const payload = {
        ...form,
        system_key: form.system_key || null,
        responsibles: form.responsibles.filter((r) => r.name?.trim()),
      };
      if (isEdit) {
        await api.put(`/maintenance/scheduled/${initial.id}`, payload);
      } else {
        await api.post("/maintenance/scheduled", payload);
      }
      onSaved();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="p-6 rounded-lg bg-zinc-900/50 border border-[#F5A623]/30 space-y-5" data-testid="task-form">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5A623]">{isEdit ? "Edit" : "New"} Task</div>
        <h3 className="text-xl font-bold mt-1" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>Program maintenance</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldGroup label="Task title">
          <input required className={inputCls} placeholder="Quarterly cleaning" value={form.title} onChange={(e) => setField("title", e.target.value)} data-testid="form-title" />
        </FieldGroup>
        <FieldGroup label="Device type">
          <select required className={inputCls} value={form.system_key} onChange={(e) => setField("system_key", e.target.value)} data-testid="form-system">
            <option value="">Select device type…</option>
            {systems.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
        </FieldGroup>
        <FieldGroup label="Device / identifier">
          <input required className={inputCls + " font-mono"} placeholder="e.g. CCTV-03 / Mac Mini Studio A" value={form.device_name} onChange={(e) => setField("device_name", e.target.value)} data-testid="form-device" />
        </FieldGroup>
        <FieldGroup label="Scheduled date">
          <input required type="date" className={inputCls} value={form.scheduled_date} onChange={(e) => setField("scheduled_date", e.target.value)} data-testid="form-date" />
        </FieldGroup>
      </div>

      <FieldGroup label="Details">
        <textarea rows={3} className={inputCls} placeholder="What needs to be done, steps, parts required…" value={form.details} onChange={(e) => setField("details", e.target.value)} data-testid="form-details" />
      </FieldGroup>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">People responsible</div>
          <button type="button" onClick={addResp} data-testid="form-add-resp" className="inline-flex items-center gap-1 text-xs text-[#F5A623] hover:underline">
            <Plus size={12} /> Add person
          </button>
        </div>
        <div className="space-y-2">
          {form.responsibles.map((r, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center" data-testid={`form-resp-row-${idx}`}>
              <input className={inputCls + " sm:col-span-4"} placeholder="Name" value={r.name} onChange={(e) => updateResp(idx, { name: e.target.value })} />
              <input className={inputCls + " sm:col-span-4"} placeholder="Email" type="email" value={r.email} onChange={(e) => updateResp(idx, { email: e.target.value })} />
              <input className={inputCls + " sm:col-span-3"} placeholder="Role (e.g. Team Lead)" value={r.role} onChange={(e) => updateResp(idx, { role: e.target.value })} />
              <button type="button" onClick={() => removeResp(idx)} className="sm:col-span-1 p-2 rounded text-zinc-500 hover:text-red-400 hover:bg-red-950/30 justify-self-start">
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <FieldGroup label="Comments">
        <textarea rows={3} className={inputCls} placeholder="Observations, access requirements, tools…" value={form.comments} onChange={(e) => setField("comments", e.target.value)} data-testid="form-comments" />
      </FieldGroup>

      <FieldGroup label="Status">
        <select className={inputCls + " max-w-xs"} value={form.status} onChange={(e) => setField("status", e.target.value)} data-testid="form-status">
          <option value="pending">Pending</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
        </select>
      </FieldGroup>

      {err && <div className="text-sm text-red-400 bg-red-950/30 border border-red-900/50 rounded-md px-3 py-2">{err}</div>}

      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={saving} data-testid="form-save-btn" className="px-4 py-2 rounded-md bg-[#F5A623] hover:bg-[#E67E22] text-zinc-950 font-bold text-sm transition-colors disabled:opacity-60">
          {saving ? "Saving…" : isEdit ? "Save changes" : "Schedule"}
        </button>
      </div>
    </form>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">{label}</label>
      {children}
    </div>
  );
}
